/**
 * Pure timeline math for a battle, from the fields the server stored at start. The server is the
 * authority: `visualRevealAt(N) = startedAt + countdown + N × interval`; results reach the client
 * a little early and the page gates rendering on these instants (boxed's model).
 */
import type {
  Battle,
  BattleEvent,
  BattleResponse,
  BattleSummary,
  BoxSummary,
} from "@loot-os/api-contract";
import {
  BATTLE_STALE_LOBBY_MS,
  BATTLE_START_COUNTDOWN_MS,
  battleRevealIntervalMs,
} from "@loot-os/api-contract";

type TimelineBattle = Pick<
  BattleSummary,
  "startedAt" | "startCountdownMs" | "revealIntervalMs" | "roundCount" | "playerCount" | "fastSpin"
>;

export function intervalMsOf(battle: TimelineBattle): number {
  return (
    battle.revealIntervalMs ??
    battleRevealIntervalMs({ playerCount: battle.playerCount, fastSpin: battle.fastSpin })
  );
}

export function countdownMsOf(battle: TimelineBattle): number {
  return battle.startCountdownMs ?? BATTLE_START_COUNTDOWN_MS;
}

/** When round N may render; null before the battle started. */
export function visualRevealAtMs(battle: TimelineBattle, round: number): number | null {
  if (!battle.startedAt) return null;
  return Date.parse(battle.startedAt) + countdownMsOf(battle) + round * intervalMsOf(battle);
}

/** When the battle completes: one interval past the last round's boundary. */
export function completeAtMs(battle: TimelineBattle): number | null {
  if (!battle.startedAt) return null;
  return (
    Date.parse(battle.startedAt) + countdownMsOf(battle) + battle.roundCount * intervalMsOf(battle)
  );
}

/**
 * The round the live timeline is on at `nowMs`: null before start, -1 during the countdown,
 * otherwise the round whose boundary has passed, capped at the last round.
 */
export function liveTimelineRoundIndex(battle: TimelineBattle, nowMs: number): number | null {
  if (!battle.startedAt) return null;
  const startMs = Date.parse(battle.startedAt) + countdownMsOf(battle);
  if (nowMs < startMs) return -1;
  const round = Math.floor((nowMs - startMs) / intervalMsOf(battle));
  return Math.min(round, battle.roundCount - 1);
}

/** After a tab-out: the round to snap to, never past what the server has revealed. */
export function resyncTargetRound(args: {
  currentRevealedRound: number;
  timelineRound: number;
  roundCount: number;
}): number {
  return Math.max(0, Math.min(args.currentRevealedRound, args.timelineRound, args.roundCount - 1));
}

/** A round arrived out of order: something was missed, refetch instead of merging. */
export function detectRoundGap(currentRevealedRound: number, eventRound: number): boolean {
  return eventRound > currentRevealedRound + 1;
}

/** Merges a `ROUND_REVEALED` event into the cached battle; returns the input on a gap. */
export function applyRoundRevealed(
  prev: BattleResponse,
  event: Extract<BattleEvent, { type: "ROUND_REVEALED" }>,
): BattleResponse {
  const { battle } = prev;
  if (event.round <= battle.currentRevealedRound) return prev;
  if (detectRoundGap(battle.currentRevealedRound, event.round)) return prev;
  const results = battle.results.slice(0, event.round);
  results[event.round] = [...event.results].sort((a, b) => a.seatIndex - b.seatIndex);
  return {
    ...prev,
    battle: {
      ...battle,
      status: battle.status === "created" ? "started" : battle.status,
      currentRevealedRound: event.round,
      results,
    },
  };
}

/** Anyone may start a lobby once it has sat `BATTLE_STALE_LOBBY_MS`. */
export function canAnyoneStart(
  battle: Pick<BattleSummary, "status" | "createdAt">,
  nowMs: number,
): boolean {
  return (
    battle.status === "created" && nowMs - Date.parse(battle.createdAt) >= BATTLE_STALE_LOBBY_MS
  );
}

export type BattleVisualStatus = "joinable" | "in-progress" | "finished";

export function battleVisualStatus(battle: Pick<BattleSummary, "status">): BattleVisualStatus {
  if (battle.status === "created") return "joinable";
  if (battle.status === "started") return "in-progress";
  return "finished";
}

/** Each seat's total item value over rounds 0..throughRound (inclusive; -1 = none). */
export function seatRunningTotals(battle: Battle, throughRound: number): number[] {
  const totals = Array.from({ length: battle.playerCount }, () => 0);
  for (let round = 0; round <= throughRound && round < battle.results.length; round++) {
    for (const open of battle.results[round] ?? []) {
      totals[open.seatIndex] = (totals[open.seatIndex] ?? 0) + open.item.value;
    }
  }
  return totals;
}

/** What every seat pays: the sum of each chosen box's price times its quantity (rounds). */
export function battlePriceCents(
  boxes: readonly Pick<BoxSummary, "id" | "priceCents">[],
  quantities: Readonly<Record<string, number>>,
): number {
  return boxes.reduce((sum, box) => sum + box.priceCents * (quantities[box.id] ?? 0), 0);
}

/** The ordered box ids a creator's picks expand to: each box repeated by its quantity. */
export function expandRounds(
  order: readonly string[],
  quantities: Readonly<Record<string, number>>,
): string[] {
  return order.flatMap((boxId) => Array.from({ length: quantities[boxId] ?? 0 }, () => boxId));
}

/** A creator's picks: quantity of rounds per box id, and the box ids in round order. */
export interface BattlePicks {
  quantities: Record<string, number>;
  order: string[];
}

/**
 * The picks the create page starts from: one round of `boxId` when the box page linked here with
 * it and the box is one the shopper can see, otherwise nothing. An id not in the list seeds
 * nothing rather than a nameless round: the box is unpublished, or not this environment's.
 */
export function initialBattlePicks(
  boxes: readonly Pick<BoxSummary, "id">[],
  boxId: string | undefined,
): BattlePicks {
  if (!boxId || !boxes.some((box) => box.id === boxId)) return { quantities: {}, order: [] };
  return { quantities: { [boxId]: 1 }, order: [boxId] };
}
